import type { PressEvents } from "@react-types/shared";
import { ProjectColoredBadge } from "@selector/shared/components";
import { clsx } from "clsx";
import type { CSSProperties, FC, ForwardedRef, PropsWithChildren, ReactElement, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { mergeProps, useFocusRing, useHover, usePress } from "react-aria";
import { useNavigate } from "react-router";
import type { ColoredLabelProps, TagProps } from "widgets";
import { AnimatedTooltip, ColoredLabel, formatDateTimeFromISO, HStack, SymbolIcon, Tag, TooltipIntent } from "widgets";
import { useDOMRef, useTooltip } from "widgets/hooks";
import { forwardRef, getCssColor } from "widgets/utils";

import { BrowserTab } from "@/app-utils/browser-tab.js";
import { capitalizeFirstLetter } from "@/app-utils/string.js";
import type { Project, User } from "@/graphql.js";
import { TileStatus } from "@/modules/core/components/settings.ts";
import { COLOR } from "@/settings/colors-settings.js";
import styleCard from "@/ui-modules/core/cards/styles/Card.module.scss";
import { getTimeTooltipMessage } from "@/ui-modules/core/tiles/utils/getTimeTooltipMessage.ts";
import { Avatar, AvatarPile } from "@/ui-modules/core/UserAvatar.js";

import style from "../styles/Tile.module.scss";

export interface TileProps {
  children?: ReactNode; // temporary as there is a known bug in the @types definitions with forwardRef
  style?: CSSProperties;
  dates?: { created?: string; modified?: string };
  users?: { creator?: User; modifier?: User };
  project?: Project;
  /**
   * The tag(s) will be place automatically:
   * - a short tag (<= 6 chars) in the header or on the right side.
   * - a long tag or multiple tags, before the footNote (after the children).
   */
  tags?: TagProps[];
  coloredLabel?: ColoredLabelProps[];
  menuButton?: ReactNode;
  title: string;
  /**
   * `null` can be used in HOC to inactivate the property.
   */
  type?: string | null;
  /**
   * Specific styling for potentially long file/folder path of parent organism name.
   * Comes after the tile type.
   */
  subtitle?: string;
  footNote?: ReactNode;
  /**
   * Use an SVG element. It will be resized automatically.
   */
  icon?: ReactElement;
  iconWidth?: number;
  /**
   * Different status for the Tile
   */
  protectionStatus?: TileStatus;
  statusDisplayName?: string; // allows to build some automated messages if needed (defaults to "entity")
  tooltip?: ReactNode;
  onPress?: PressEvents["onPress"];
  lightweight?: boolean;
  /**
   * If `onPress` is set, this won't be activated.
   */
  to?: string;
  forceExternal?: boolean;
  /**
   * This will automatically set the Tile as an Event Tile.
   * Set it to null to prevent showing the bullet.
   */
  eventStatusBulletColor?: CSSProperties["color"];
  /**
   * Only used at the moment for Playbook icon in the case of Event tile.
   * Playbook tiles does have the icon on the left side, like other tiles.
   */
  rightIcon?: ReactNode;
  titleTrailer?: ReactNode;
}

/**
 * A Generic Tile.
 * Don't use it directly and check what is exposed from "core/tiles" package.
 * Please see the documentation to see what kind of tiles can be created with it.
 * @deprecated use ItemTile instead
 */
const TileRender = (props: TileProps, ref: ForwardedRef<HTMLDivElement>) => {
  const isProtected = props.protectionStatus != undefined && props.protectionStatus !== TileStatus.Normal;
  const tooltip = isProtected
    ? getProtectedEntityExplanation(props.protectionStatus, props.statusDisplayName)
    : props.tooltip;

  const hasHeader = useMemo<boolean>(
    () => !props.lightweight && !!(props.dates?.created || props.users?.creator || props.project),
    [props.dates, props.project, props.users, props.lightweight],
  );
  const [specialKeyPressed, setSpecialKeyPress] = useState(false);

  // Link
  const navigate = useNavigate();
  const isExternalLink = useMemo(() => {
    if (!props.to) {
      return false;
    }

    if (typeof props.to === "string") {
      return /^https?:\/\//.test(props.to);
    }

    return false;
  }, [props.to]);
  const openInNewTab = useMemo(() => {
    return props.forceExternal || specialKeyPressed || isExternalLink;
  }, [props.forceExternal, isExternalLink, specialKeyPressed]);

  const isDisabled = useMemo(() => isProtected || !(props.onPress ?? props.to), [isProtected, props.onPress, props.to]);

  const { pressProps } = usePress({
    isDisabled,
    onPress:
      props.onPress ??
      (props.to
        ? (e) => {
            if (props.to) {
              if (e.ctrlKey || e.metaKey || openInNewTab) {
                window.open(isExternalLink ? props.to : BrowserTab.createUrl(props.to));
              } else {
                navigate(props.to);
              }
            }
          }
        : undefined),
  });

  // Tooltip & Events
  const triggerRef = useDOMRef<HTMLDivElement>(ref);
  const { hoverProps, isHovered } = useHover({ isDisabled: isProtected });
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    isDisabled: !tooltip,
  });
  const { focusProps, isFocusVisible } = useFocusRing();

  // TODO: Tab accessibility effect + ref bellow/

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => setSpecialKeyPress(isHovered && (e.ctrlKey || e.metaKey));
    const onKeyUp = () => setSpecialKeyPress(false);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [isHovered]);

  return (
    <>
      <div
        ref={triggerRef}
        className={clsx(style.tile, {
          [style.tile_protected]: isProtected,
          [style.tile_default]: !(props.eventStatusBulletColor !== undefined || props.rightIcon !== undefined),
          [style.tile_event]: props.eventStatusBulletColor !== undefined || (props.rightIcon && hasHeader), // null is a valid value
          [style.tile_hover]: isHovered && !isProtected && (props.onPress || props.to),
          [style.tile_focus]: isFocusVisible,
        })}
        {...mergeProps(pressProps, triggerProps, hoverProps, focusProps)}
        role={!isDisabled ? "button" : undefined}
        tabIndex={!isDisabled || !!tooltip ? 0 : undefined}
        style={props.style}
      >
        {props.icon && !props.rightIcon && (
          <div className={style.icon} aria-hidden style={{ minWidth: props.iconWidth }}>
            {props.icon}
          </div>
        )}
        {/* This is a Playbook Tile (without header for Playbooks) */}
        {props.rightIcon && !hasHeader && <div className={style.playbook_icon}>{props.rightIcon}</div>}
        <div className={style.tile_content}>
          {hasHeader && (
            <TileHeader
              dates={props.dates}
              users={props.users}
              project={props.project}
              //tags={hasSmallTag ? props.tags : undefined}
              menuButton={props.menuButton}
              eventStatusBulletColor={props.eventStatusBulletColor}
            />
          )}
          <ContentTile
            {...{ ...props, rightIcon: props.rightIcon && !hasHeader ? null : props.rightIcon, isProtected }}
            hocGrid
            type={props.lightweight ? undefined : props.type}
          />
          {props.footNote && !props.lightweight && <div className={style.description}>{props.footNote}</div>}
        </div>
        {!hasHeader && props.menuButton}
        {isExternalLink && <SymbolIcon icon={"open_in_new"} aria-hidden={true} className={style.icon_external} />}
      </div>
      <AnimatedTooltip
        targetRef={triggerRef}
        state={state}
        domProps={tooltipProps}
        intent={isProtected ? TooltipIntent.Danger : TooltipIntent.Default}
      >
        {tooltip}
      </AnimatedTooltip>
    </>
  );
};
export const Tile = forwardRef(TileRender);

export type ContentProps = Pick<
  TileProps,
  | "title"
  | "type"
  | "subtitle"
  | "tags"
  | "coloredLabel"
  | "statusDisplayName"
  | "rightIcon"
  | "icon"
  | "protectionStatus"
  | "titleTrailer"
>;
export interface ContentTileProps extends PropsWithChildren<ContentProps> {
  /**
   * @private
   * If set to true, the HOC will define the grid. Keep the name areas compatible.
   */
  hocGrid?: true;
  isProtected?: boolean;
}

/**
 * @deprecated use ItemTile instead
 */
const ContentTileRender = (props: ContentTileProps, ref: ForwardedRef<HTMLDivElement>) => {
  const content = (
    <div className={style.tile_horizontal_content} ref={ref}>
      <div className={style.tile_content}>
        <div className={style.title}>
          {props.isProtected ? (
            <HStack spacing={3}>
              <SymbolIcon
                icon={props.protectionStatus === TileStatus.Deleted ? "delete" : "security"}
                style={{ color: COLOR.DARKISH_PINK.BORDER }}
                size={16}
              />
              {!props.title
                ? `[${props.protectionStatus === TileStatus.Deleted ? "Deleted" : "Restricted"} ${capitalizeFirstLetter(
                    props.statusDisplayName?.toLowerCase() ?? "Content",
                  )}]`
                : props.title}
            </HStack>
          ) : props.titleTrailer ? (
            <HStack spacing={3}>
              {props.title}
              {props.titleTrailer}
            </HStack>
          ) : (
            props.title
          )}
        </div>
        {props.type && <div className={style.type}>{props.type}</div>}
        {props.subtitle && (
          <div className={style.path}>
            <span className={style.left_ellipsed}>{props.subtitle}</span>
          </div>
        )}
        {props.children && <div>{props.children}</div>}
        {props.tags?.length && (
          <div className={styleCard.tags_list} style={{ marginTop: 5 }}>
            {props.tags.map((tag, idx) => (
              <Tag {...tag} key={`tile-tag-${idx}`} />
            ))}
          </div>
        )}
        {props.coloredLabel?.length && (
          <div className={styleCard.tags_list} style={{ marginTop: 5 }}>
            {props.coloredLabel.map((coloredLabel, idx) => (
              <ColoredLabel {...coloredLabel} key={`tile-coloredLabel-${idx}`} />
            ))}
          </div>
        )}
      </div>
      {/* This is an event */}
      {props.rightIcon && <div className={style.playbook_icon}>{props.rightIcon}</div>}
    </div>
  );

  if (props.hocGrid) {
    return content;
  }

  return (
    <div className={style.tile}>
      {props.icon && !props.rightIcon && (
        <div className={style.icon} aria-hidden>
          {props.icon}
        </div>
      )}
      <div className={style.tile_content}>{content}</div>
    </div>
  );
};

/**
 * @deprecated use ItemTile instead
 */
export const ContentTile = forwardRef(ContentTileRender);

function getProtectedEntityExplanation(status?: TileStatus, type?: string): string {
  const entityText = type?.toLowerCase() || "entity";

  switch (status) {
    case TileStatus.Deleted:
      return `This ${entityText} has been deleted.`;
    case TileStatus.RestrictedVisibility:
      return `The access to this ${entityText} is restricted, you don't have the permission to see more details.`;
    case TileStatus.NotLicensed:
      return `This ${entityText} is not accessible because of license restrictions.`;
    case TileStatus.RequiresContext:
      return `This ${entityText} cannot be executed from here.`;
    case TileStatus.Disabled:
      return `This ${entityText} is disabled.`;
  }

  return "";
}

type TileHeaderProps = Pick<
  TileProps,
  "dates" | "users" | "project" | "tags" | "menuButton" | "eventStatusBulletColor"
>;

const TileHeader: FC<TileHeaderProps> = (props) => {
  const date = useMemo(() => {
    if (!props.dates?.created && !props.dates?.modified) return null;
    // modification date has the priority on date creation
    const date = props.dates?.modified ?? props.dates?.created;

    return date ? formatDateTimeFromISO(date) : null;
  }, [props.dates]);

  const triggerRef = useRef<HTMLDivElement>(null);
  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {});

  const bulletStyle = useMemo(
    () =>
      props.eventStatusBulletColor
        ? ({ "--bullet-color": getCssColor(props.eventStatusBulletColor) } as CSSProperties)
        : undefined,
    [props.eventStatusBulletColor],
  );

  return (
    <div className={style.header}>
      <div className={style.date}>
        {bulletStyle && <div className={style.bullet} style={bulletStyle} />}
        {date}
      </div>
      <div className={style.header_content}>
        {props.users && (
          <>
            <AvatarPile ref={triggerRef} domProps={triggerProps}>
              {props.users.creator && <Avatar user={props.users.creator} />}
              {props.users.modifier && props.users.creator?.id !== props.users.modifier.id && (
                <Avatar user={props.users.modifier} />
              )}
            </AvatarPile>
            <AnimatedTooltip targetRef={triggerRef} state={state} domProps={tooltipProps} offset={4}>
              {getTimeTooltipMessage({
                creator: props.users?.creator,
                modder: props.users?.modifier,
                creationDate: props.dates?.created,
                modificationDate: props.dates?.modified,
              })}
            </AnimatedTooltip>
          </>
        )}
        <ProjectColoredBadge project={props.project} size={"small"} noLink />
        {props.tags && props.tags.length === 1 && <Tag {...props.tags[0]} />}
        {props.menuButton}
      </div>
    </div>
  );
};
